<template>
  <!-- The POS code input form -->
  <q-item
    v-if="$register && !checkout && $q.screen.gt.xs"
    class="bg-responsive"
  >
    <q-item-section avatar>
      <q-avatar
        color="primary"
        text-color="white"
      >
        %
      </q-avatar>
    </q-item-section>
    <q-item-section>
      <q-item-label class="row items-center">
        <q-chip
          v-if="!viewOnly && !isCheckoutCodeInputActive"
          style="border-width: 0;"
          outline
          ripple
          icon="add"
          :label="$t('checkout.actions.addGiftCardVoucherPromoCode')"
          size="md"
          clickable
          data-test-id="cart-add-checkout-code"
          @click="isCheckoutCodeInputActive = true"
        />
        <q-chip
          v-else-if="!viewOnly"
          square
          outline
          style="border: none;"
        >
          <q-input
            v-model.trim="checkoutCodeUid"
            :label="computedInputLabel"
            :stack-label="false"
            :outlined="false"
            borderless
            autofocus
            hide-bottom-space
            :loading="checkoutCodeInputLoading"
            :error="!!errorMessage"
            data-test-id="pos-checkout-code-input"
            @update:model-value="findCheckoutCodeOnInput"
            @keydown.enter.prevent="findCheckoutCodeOnEnter"
            @focusout="isCheckoutCodeInputActive = checkoutCodeUid === '' ? false : isCheckoutCodeInputActive"
          />
        </q-chip>
        <q-chip
          v-for="voucher in order.uniqueAppliedVouchers"
          :key="voucher.typeName + voucher.id"
          square
          :removable="!viewOnly && voucher.canRemove"
          dense
          :color="voucher.color"
          text-color="white"
          :label="(voucher.typeName === $tc('voucher.voucher', 1) ? voucher.voucherType.name : voucher.name) + ( voucher.isReward ? ` (${voucher.voucherType.pointsCost})` : '')"
          size="md"
          @remove="voucher.autoApply = false; order.applyRedemptions()"
        />
      </q-item-label>
    </q-item-section>
    <q-item-section
      v-if="order.appliedVoucherAmount"
      side
    >
      <div class="text-subtitle2 col-auto text-red">
        -
        <i18n-n
          class="text-bold text-red"
          tag="span"
          :value="order.appliedVoucherAmount"
          format="currency"
          scope="global"
          data-test-id="cart-voucher-applied-value"
        />
      </div>
    </q-item-section>
    <voucher-package-dialog
      v-if="!viewOnly"
      ref="voucherPackageDialog"
      :order="order"
      @add-voucher="voucher => $emit('add-voucher', voucher)"
      @add-voucher-package="voucherPackage => $emit('add-voucher-package', voucherPackage)"
    />
  </q-item>
  <!-- The non-POS (Kiosk, consumer) code input form -->
  <q-item
    v-else
    :class="{
      'q-px-sm': isDrawer,
      'q-pa-none': checkout
    }"
  >
    <q-item-section>
      <q-item-label
        class="row"
      >
        <!-- Code input field -->
        <q-input
          v-if="!viewOnly"
          v-model.trim="checkoutCodeUid"
          :autofocus="autofocus"
          :label="computedInputLabel"
          :stack-label="false"
          outlined
          :dense="isDrawer || $register"
          hide-bottom-space
          class="full-width"
          :error="!!errorMessage"
          :loading="checkoutCodeInputLoading"
          @update:model-value="findCheckoutCodeOnInput"
          @keydown.enter.prevent="findCheckoutCodeOnEnter"
          @focusout="isCheckoutCodeInputActive = checkoutCodeUid === '' ? false : isCheckoutCodeInputActive"
        >
          <template #append>
            <!-- Apply button -->
            <q-btn
              color="secondary"
              :text-color="$isColorDark(currentSite.secondaryColor) ? 'white' : 'black'"
              dense
              :size="isDrawer || $register ? 'sm' : undefined"
              rounded
              class="q-px-sm"
              :label="$t('actions.apply')"
              :disable="checkoutCodeInputLoading"
              @click.stop="findCheckoutCodeOnEnter"
            />
          </template>
        </q-input>
      </q-item-label>
      <q-item-label class="row">
        <template
          v-for="voucher in order.uniqueAppliedVouchers"
        >
          <q-chip
            v-if="!voucher.isReward"
            :key="voucher.typeName + voucher.id"
            square
            :removable="!viewOnly && voucher.canRemove"
            dense
            :color="voucher.color"
            :text-color="textColor(voucher.color)"
            :label="(voucher.typeName === $tc('voucher.voucher', 1) ? voucher.voucherType.name : voucher.name) + ( voucher.isReward ? ` (${voucher.voucherType.pointsCost})` : '')"
            :size="isDrawer ? 'sm' : 'md'"
            @remove="voucher.autoApply = false; order.applyRedemptions()"
          />
        </template>
      </q-item-label>
    </q-item-section>
    <voucher-package-dialog
      v-if="!viewOnly"
      ref="voucherPackageDialog"
      :order="order"
      @add-voucher="voucher => $emit('add-voucher', voucher)"
      @add-voucher-package="voucherPackage => $emit('add-voucher-package', voucherPackage)"
    />
  </q-item>
</template>

<script>
import { Notify } from 'quasar'
import i18n from 'src/i18n'
import Card from 'src/models/card/Card'
import CeaCard from 'src/models/cea-card/CeaCard'
import GiftCard, { GIFT_CARD_STATES } from 'src/models/gift-card/GiftCard'
import MeerkatMovie from 'src/models/meerkat-movie/MeerkatMovie'
import IssuedVoucherPackage from 'src/models/voucher/IssuedVoucherPackage'
import Voucher, { VOUCHER_STATES } from 'src/models/voucher/Voucher'
import VoucherPackageDialog from 'src/modules/checkout/components/VoucherPackageDialog'

const CHECKOUT_CODE_PREFIXES = Object.freeze({
  GIFT_CARD: 'G',
  VOUCHER: 'V',
  MEERKAT_MOVIE: 'MM',
  VOUCHER_PACKAGE: 'VP'
})

const MEERKAT_MOVIE_ERROR_MESSAGES = Object.freeze({
  ALREADY_USED: 'ALREADY USED'
})

export default {
  name: 'CheckoutCodeInput',
  components: {
    VoucherPackageDialog
  },
  configuration: ['currentSite', 'currentCircuit', 'discounts', 'voucherTypes'],
  context: ['currentUser', 'currentRegisterClient'],
  inject: ['order'],
  props: {
    autofocus: {
      type: Boolean,
      required: false,
      default: false
    },
    viewOnly: {
      type: Boolean,
      required: false,
      default: false
    },
    isDrawer: {
      type: Boolean,
      required: false,
      default: false
    },
    checkout: {
      type: Boolean,
      required: false,
      default: false
    },
    label: {
      type: String,
      required: false,
      default: i18n.global.t('checkout.actions.addGiftCardVoucherPromoCode')
    }
  },
  emits: ['add-voucher', 'add-voucher-package', 'input-checkout-code'],
  data () {
    return {
      isCheckoutCodeInputActive: false,
      checkoutCodeUid: '',
      lastFetchedCheckoutCodeUid: '',
      checkoutCodeInputLoading: false,
      errorMessage: ''
    }
  },
  computed: {
    computedInputLabel () {
      return this.errorMessage || this.label
    },
    meerkatMovieVoucherType () {
      return this.currentSite.meerkatMovieEnabledValue ? _.find(this.voucherTypes.data, { isMeerkatMovieValue: true }) : null
    }
  },
  methods: {
    async findVoucher (uid) {
      const voucher = new Voucher({}, {
        queryName: 'voucherByUid',
        issuedVoucherPackage: {
          vouchers: {
            voucherType: {
              fields: 'all',
              itemVariation: true
            }
          }
        },
        variables: { uid }
      })
      await voucher.ensureLoaded()
      if (voucher?.issuedVoucherPackage) {
        this.$refs.voucherPackageDialog.show(voucher.issuedVoucherPackage)
        this.resetCodeInput('')
        return true
      } else if (voucher?.uid) {
        if (voucher.state === VOUCHER_STATES.EXPIRED) {
          Notify.create({
            timeout: 3000,
            message: this.$i18n.t('voucher.errors.hasExpired'),
            type: 'negative',
            position: 'top'
          })
          this.resetCodeInput(this.$i18n.t('voucher.errors.hasExpired'))
          return false
        } else if (voucher.state !== VOUCHER_STATES.VALID) {
          Notify.create({
            timeout: 3000,
            message: 'Voucher is not valid',
            type: 'negative',
            position: 'top'
          })
          this.resetCodeInput('Voucher is not valid')
          return false
        } else {
          this.order.applyIndependentVoucher(voucher)
          this.$emit('add-voucher', voucher)
        }
        this.resetCodeInput('')
        return true
      }
      this.resetCodeInput(this.$i18n.t('voucher.errors.notFound'))
      return false
    },
    // Fetches, validates, and adds the gift card to the order
    async findGiftCard (uid) {
      const giftCard = new GiftCard({
        options: {
          queryName: 'giftCardByUid',
          initialValue: 'default',
          balance: 'default',
          variables: {
            uid
          }
        }
      })

      await giftCard.fetch(true)

      if (!giftCard.isRemote) {
        this.resetCodeInput('Gift Card Not Found')
        return false
      }

      if (giftCard.balance === 0) {
        this.resetCodeInput('Gift Card Balance: $0')
        return false
      }

      if (giftCard.state === GIFT_CARD_STATES.EXPIRED) {
        this.resetCodeInput('Gift Card has expired')
        return false
      }

      if (giftCard.state !== GIFT_CARD_STATES.VALID) {
        this.resetCodeInput('Gift Card is not valid')
        return false
      }

      this.order.addGiftCard(giftCard)
      this.resetCodeInput('')

      return true
    },
    async findVoucherPackage (uid) {
      const issuedVoucherPackage = new IssuedVoucherPackage({}, {
        queryName: 'issuedVoucherPackageByUid',
        vouchers: {
          voucherType: {
            fields: 'all',
            itemVariation: true
          }
        },
        variables: { uid }
      })
      await issuedVoucherPackage.ensureLoaded()
      if (issuedVoucherPackage?.uid) {
        this.$refs.voucherPackageDialog.show(issuedVoucherPackage)
        this.resetCodeInput('')
        return true
      } else {
        this.resetCodeInput('Invalid Voucher Package')
        return false
      }
    },
    async validateCeaCard (uid) {
      const ceaCard = new CeaCard({ cardNumber: uid })
      await ceaCard.save()
      ceaCard.destroy()
      if (!ceaCard.valid) {
        return
      }

      const isValidCeaOrder = this.order.isValidCeaOrder()
      if (this.order.ceaCardApplied) {
        this.resetCodeInput('CEA card has already been applied.')
        return false
      } else if (!isValidCeaOrder) {
        this.resetCodeInput('Card valid, order does not contain any redeemable CEA tickets.')
        return false
      } else {
        this.order.addIndependentVoucher(new Voucher({ voucherTypeId: this.ceaCardVoucherType().id }))
        this.resetCodeInput('')
      }
      return true
    },
    async validateMeerkatMovie () {
      if (!this.meerkatMovieVoucherType || !this.checkoutCodeUid) return

      const movieEdiCode = this.order.meerkatMovieOrderItems[0].ticket?.showing?.movie?.dcmEdiMovieId

      if (!movieEdiCode) {
        this.resetCodeInput('Cannot use Meerkat Movies voucher for this movie.')
        return false
      }

      const meerkatMovie = new MeerkatMovie({ pinCode: this.checkoutCodeUid, movieEdiCode })

      await meerkatMovie.save()
      if (!meerkatMovie.valid) {
        if (meerkatMovie.error === MEERKAT_MOVIE_ERROR_MESSAGES.ALREADY_USED) {
          Notify.create({
            timeout: 3000,
            message: 'Meerkat Movies voucher has already been used.',
            type: 'negative',
            position: 'top'
          })
        } else {
          this.resetCodeInput('Invalid Meerkat Movies voucher.')
        }
        meerkatMovie.destroy()
        return false
      }

      if (this.order.hasMeerkatMovieVoucher) {
        Notify.create({
          timeout: 3000,
          message: 'Only one Meerkat Movies voucher can be used per order.',
          type: 'negative',
          position: 'top'
        })
        meerkatMovie.destroy()
        return false
      }
      const meerkatMovieVoucher = new Voucher({ id: meerkatMovie.voucherId })
      await meerkatMovieVoucher.fetch()
      if (_.isNil(meerkatMovieVoucher)) {
        this.resetCodeInput('Meerkat Movies voucher not found.')
        meerkatMovie.destroy()
        return false
      }
      this.order.addIndependentVoucher(meerkatMovieVoucher)
      meerkatMovie.destroy()

      if (!this.order.hasMeerkatMovieVoucherApplied) {
        Notify.create({
          timeout: 3000,
          message: 'Meerkat Movies voucher requires two tickets to be purchased.',
          type: 'negative',
          position: 'top'
        })
      }
      this.resetCodeInput('')
      return true
    },
    ceaCardVoucherType () {
      return this.currentSite.ceaCardEnabled ? _.find(this.voucherTypes.data, { isCeaCard: true }) : null
    },
    async findCard (uid) {
      const card = new Card({
        options: {
          queryName: 'cardByUid',
          user: 'default',
          issuedVoucherPackage: {
            vouchers: {
              voucherType: {
                fields: 'all',
                itemVariation: true
              }
            }
          },
          voucher: {
            voucherType: {
              fields: 'all',
              itemVariation: true
            }
          },
          giftCard: {
            initialValue: 'default',
            balance: 'default',
            card: 'default'
          },
          variables: {
            uid
          }
        }
      })

      await card.fetch(true)

      // Card may have null objects if the request failed
      if (card.voucher?.uid) {
        this.findVoucher(card.voucher.uid)
      } else if (card.giftCard?.uid) {
        this.findGiftCard(card.giftCard.uid)
      } else if (card.issuedVoucherPackage?.uid) {
        this.findVoucherPackage(card.issuedVoucherPackage.uid)
      } else {
        // if could not find card, assume code is a discount code
        this.checkoutCodeInputLoading = true
        const resolvedPromoCode = await this.discounts.matchingOnlineDiscountCode(this.checkoutCodeUid)
        if (!resolvedPromoCode) {
          this.resetCodeInput('Code Not Found')
          return false
        }
        this.order.addIndependentDiscount(resolvedPromoCode)
        this.resetCodeInput('')
        return true
      }
    },
    findCheckoutCodeOnInput () {
      if (!this.checkoutCodeUid) {
        this.resetCodeInput('')
        return
      }

      this.$emit('input-checkout-code', true)

      if (!this._debouncedFindCheckoutCode) {
        this._debouncedFindCheckoutCode = _.debounce(this.findCheckoutCode, 2500) // 2.5 seconds
      }
      this._debouncedFindCheckoutCode()
    },
    findCheckoutCodeOnEnter () {
      this.findCheckoutCode()
    },
    async findCheckoutCode () {
      try {
        if (this.checkoutCodeInputLoading) return
        if (!this.checkoutCodeUid) {
          this.resetCodeInput('')
          return
        }
        if (this.checkoutCodeUid === this.lastFetchedCheckoutCodeUid) return

        this.checkoutCodeInputLoading = true
        const ceaCardRegex = /^\d{9}$/

        let cardable = null
        if (_.startsWith(this.checkoutCodeUid, CHECKOUT_CODE_PREFIXES.VOUCHER_PACKAGE)) {
          cardable = await this.findVoucherPackage(this.checkoutCodeUid)
        } else if (_.startsWith(this.checkoutCodeUid, CHECKOUT_CODE_PREFIXES.GIFT_CARD)) {
          cardable = await this.findGiftCard(this.checkoutCodeUid)
        } else if (_.startsWith(this.checkoutCodeUid, CHECKOUT_CODE_PREFIXES.VOUCHER)) {
          cardable = await this.findVoucher(this.checkoutCodeUid)
        } else if (_.startsWith(this.checkoutCodeUid, CHECKOUT_CODE_PREFIXES.MEERKAT_MOVIE) && this.meerkatMovieVoucherType) {
          cardable = await this.findVoucher(this.checkoutCodeUid)
        } else if (this.ceaCardVoucherType && ceaCardRegex.test(this.checkoutCodeUid)) {
          cardable = await this.validateCeaCard(this.checkoutCodeUid)
        }
        if (this.meerkatMovieVoucherType && !cardable) {
          cardable = await this.validateMeerkatMovie()
        }
        if (!cardable) {
          cardable = await this.findCard(this.checkoutCodeUid)
        }
        this.lastFetchedCheckoutCodeUid = this.checkoutCodeUid
      } catch (error) {
        this.resetCodeInput(error)
      } finally {
        // This should have already been triggered by resetCodeInput, but just in case emitting here too
        this.$emit('input-checkout-code', false)
      }
    },
    resetCodeInput (errorMessage) {
      this.checkoutCodeInputLoading = false
      this.$emit('input-checkout-code', false)

      this.errorMessage = errorMessage
      if (!errorMessage) {
        this.checkoutCodeUid = ''
      }
    },
    textColor (color) {
      // when return hex code
      if (color.startsWith('#')) return this.$isColorDark(color) ? 'white' : 'black'
      // when return value from colorOptions
      const colorHex = this.$getSiteColorHex(this.currentSite, color)
      if (!colorHex) return false
      return this.$isColorDark(colorHex) ? 'white' : 'black'
    }
  }
}
</script>
